import { useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { green } from '@mui/material/colors';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { useTranslation } from 'react-i18next';
import OptimusButton from '../../common/library/Button/Button';
import { TestIds } from '../../assets/testIds';
import { CoachComingSoonModal } from './CoachComingSoonModal';
import { useAppSelector } from '../../store/hooks';
import { selectMemberSession, selectMemberInformation } from '../../store/slices/onboardingSlice';
import { notifyBetaCoach, joinBetaCoach } from '../../api/coachAPI';
import { Logger } from '../../config/logging/logger';

const logger = Logger.create('CoachComingSoon');

// Which coming-soon CTA the member clicked — purely a UI concern (decides which
// confirmation modal to show); not sent to the backend.
type BetaInterestCta = 'joinBeta' | 'notifyMe';

// Identifies which confirmation modal is open, if any.
type ActiveModal = BetaInterestCta | null;

// The three feature chips shown beneath the description. Keyed by translation
// key so copy stays centralized in the i18n bundle.
const BENEFIT_KEYS = [
  'coach.comingSoon.benefits.coachIntelligence',
  'coach.comingSoon.benefits.knowledgeBase',
  'coach.comingSoon.benefits.personalized',
];

/**
 * Placeholder shown on the AI Business Coach tab while the feature is gated
 * behind the `aiCoachBetaAccess` flag. Mirrors the Figma "Coming Soon Splash
 * Card". Each CTA opens a confirmation modal acknowledging the user's interest.
 */
export function CoachComingSoon() {
  const theme = useTheme();
  const { t } = useTranslation();
  const { memberId } = useAppSelector(selectMemberSession);
  const { email } = useAppSelector(selectMemberInformation);
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);

  // Record the member's interest, then open the confirmation modal. The modal
  // opens regardless of the API outcome — the writes are best-effort and we
  // don't want a failed request to block the acknowledgement. "Join beta"
  // notifies the team by email (via event-service → SendGrid); "Notify me"
  // records the member in the beta-notify list.
  const handleInterest = (cta: BetaInterestCta) => {
    setActiveModal(cta);
    if (memberId && email) {
      if (cta === 'joinBeta') {
        joinBetaCoach({ memberId, email }).catch((error) => {
          logger.error(`Failed to notify coach beta join: ${(error as Error).message}`);
        });
      } else {
        notifyBetaCoach({ memberId, email }).catch((error) => {
          logger.error(`Failed to register coach beta notify: ${(error as Error).message}`);
        });
      }
    }
  };

  return (
    <>
      <Box
        data-testid={TestIds.coach.comingSoonCard}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'flex-start' },
          gap: theme.spacing(4),
          padding: theme.spacing(4),
          borderRadius: theme.spacing(1.5),
          border: `1px solid ${theme.palette.grey[200]}`,
          background: `linear-gradient(to right, ${theme.palette.common.white}, ${theme.palette.brand.surface})`,
          boxShadow:
          '0px 1px 4px rgba(0, 0, 0, 0.12), 0px 3px 2px rgba(0, 0, 0, 0.14), 0px 3px 1.5px rgba(0, 0, 0, 0.20)',
        }}
      >
        {/* Content column */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(2.5),
            flex: '1 0 0',
            minWidth: 0,
          }}
        >
          {/* Coming Soon badge */}
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              alignSelf: 'flex-start',
              padding: `${theme.spacing(0.75)} ${theme.spacing(1.5)}`,
              borderRadius: theme.spacing(12.5),
              // Soft-green "Coming Soon" badge colors from the Figma design,
              // mapped to the nearest MUI green palette shades.
              backgroundColor: green[50],
              border: `1px solid ${green[200]}`,
            }}
          >
            <Typography
              variant="labelSmProminent"
              sx={{
                color: green[900],
                textTransform: 'uppercase',
              }}
            >
              {t('coach.comingSoon.badge')}
            </Typography>
          </Box>

          {/* Title + description */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1.5) }}>
            <Typography
              variant="h12"
              sx={{ color: theme.palette.brand.primary }}
            >
              {t('coach.comingSoon.title')}
            </Typography>
            <Typography
              variant="bodyMd"
              sx={{ color: theme.palette.grey[600] }}
            >
              {t('coach.comingSoon.description')}
            </Typography>
          </Box>

          {/* Benefit chips */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: theme.spacing(1) }}>
            {BENEFIT_KEYS.map((benefitKey) => (
              <Box
                key={benefitKey}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: `${theme.spacing(0.75)} ${theme.spacing(1.25)}`,
                  borderRadius: theme.spacing(0.75),
                  backgroundColor: theme.palette.common.white,
                  border: `1px solid ${theme.palette.grey[200]}`,
                }}
              >
                <Typography
                  variant="labelMd"
                  sx={{ color: theme.palette.brand.primary }}
                >
                  {t(benefitKey)}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* CTAs */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: theme.spacing(1.25) }}>
            <OptimusButton
              data-testid={TestIds.coach.comingSoonJoinBetaBtn}
              customVariant="primary"
              buttonText={`${t('coach.comingSoon.joinBeta')}`}
              leadingIcon={<RocketLaunchIcon fontSize="small" />}
              fullWidth={false}
              onClick={() => handleInterest('joinBeta')}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            />
            <OptimusButton
              data-testid={TestIds.coach.comingSoonNotifyBtn}
              customVariant="secondaryGray"
              buttonText={`${t('coach.comingSoon.notifyMe')}`}
              leadingIcon={<EmailOutlinedIcon fontSize="small" />}
              fullWidth={false}
              onClick={() => handleInterest('notifyMe')}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            />
          </Box>
        </Box>

        {/* Visual: sparkle icon in a circular container */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: theme.spacing(14.5),
              height: theme.spacing(14.5),
              borderRadius: '50%',
              backgroundColor: theme.palette.common.white,
              border: `1px solid ${theme.palette.grey[200]}`,
              boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.05)',
            }}
          >
            <AutoAwesomeIcon
              sx={{ fontSize: theme.spacing(8), color: theme.palette.brand.primary }}
            />
          </Box>
        </Box>
      </Box>

      <CoachComingSoonModal
        open={activeModal === 'joinBeta'}
        onClose={() => setActiveModal(null)}
        title={t('coach.comingSoon.joinBetaModal.title')}
        body={t('coach.comingSoon.joinBetaModal.body')}
      />
      <CoachComingSoonModal
        open={activeModal === 'notifyMe'}
        onClose={() => setActiveModal(null)}
        title={t('coach.comingSoon.notifyMeModal.title')}
        body={t('coach.comingSoon.notifyMeModal.body')}
      />
    </>
  );
}
